<template>
  <button v-if="hasButton" type="button" @click="openModal" class="btn" :class="labelClass">
    {{ label }}
  </button>

  <TransitionRoot :show="isOpen" as="template">
    <Dialog
        as="div"
        @close="closeModal"
        class="relative z-[9999]"
        v-if="disableBackdrop === false"
    >
      <TransitionChild
          :enter="noFade ? '' : 'duration-300 ease-out'"
          :enter-from="noFade ? '' : 'opacity-0'"
          :enter-to="noFade ? '' : 'opacity-100'"
          :leave="noFade ? '' : 'duration-300 ease-in'"
          :leave-from="noFade ? '' : 'opacity-100'"
          :leave-to="noFade ? '' : 'opacity-0'"
      >
        <div
            class="fixed inset-0 bg-slate-900/50 backdrop-filter backdrop-blur-sm"
        />
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto">
        <div
            class="flex justify-center text-center p-6 h-full"
            :class="[centered ? 'items-center' : 'items-start']"
        >
          <TransitionChild
              as="template"
              :enter="noFade ? '' : 'duration-300  ease-out'"
              :enter-from="noFade ? '' : 'opacity-0 scale-95'"
              :enter-to="noFade ? '' : 'opacity-100 scale-100'"
              :leave="noFade ? '' : 'duration-200 ease-in'"
              :leave-from="noFade ? '' : 'opacity-100 scale-100'"
              :leave-to="noFade ? '' : 'opacity-0 scale-95'"
          >
            <DialogPanel
                class="w-full flex flex-col transform overflow-hidden rounded-md bg-white dark:bg-slate-800 text-left align-middle shadow-xl transition-all"
                :class="sizeClass"
            >
              <div
                  class="relative py-2 lg:py-4 px-5 text-white flex justify-between"
                  :class="themeClass"
              >
                <h2
                    v-if="title"
                    class="capitalize leading-6 tracking-wider font-medium text-sm lg:text-base text-white dark:text-white"
                >
                  {{ title }}
                </h2>
                <button @click="closeModal" class="text-base lg:text-[22px]">
                  <Icon icon="heroicons-outline:x"/>
                </button>
              </div>
              <div
                  class="px-6 py-8 custom-scrollbar overflow-y-auto flex-1"
                  :class="`${contentClass} ${themeSettingsStore.isDark ? 'custom-scrollbar-dark' : 'custom-scrollbar-light'}`"
              >
                <slot/>
              </div>
              <div
                  class="px-4 py-3 flex justify-end space-x-3 rtl:space-x-reverse border-t border-slate-100 dark:border-slate-700"
                  :class="`${footerClass}`"
                  v-if="$slots.footer"
              >
                <slot name="footer"></slot>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
    <Dialog as="div" class="relative z-[9999]" v-else>
      <div class="fixed inset-0 overflow-y-auto">
        <div
            class="flex min-h-full justify-center text-center p-6"
            :class="centered ? 'items-center' : 'items-start '"
        >
          <TransitionChild
              as="template"
              :enter="noFade ? '' : 'duration-300  ease-out'"
              :enter-from="noFade ? '' : 'opacity-0 scale-95'"
              :enter-to="noFade ? '' : 'opacity-100 scale-100'"
              :leave="noFade ? '' : 'duration-200 ease-in'"
              :leave-from="noFade ? '' : 'opacity-100 scale-100'"
              :leave-to="noFade ? '' : 'opacity-0 scale-95'"
          >
            <DialogPanel
                class="w-full transform overflow-hidden rounded-md bg-white dark:bg-slate-800 text-left align-middle shadow-xl transition-all"
                :class="sizeClass"
            >
              <div
                  class="relative overflow-hidden py-4 px-5 text-white flex justify-between"
                  :class="themeClass"
              >
                <h2
                    v-if="title"
                    class="capitalize leading-6 tracking-wider font-medium text-base text-white"
                >
                  {{ title }}
                </h2>
                <button @click="closeModal" class="text-[22px]">
                  <Icon icon="heroicons-outline:x"/>
                </button>
              </div>
              <div
                  class="px-6 py-8 custom-scrollbar overflow-y-auto flex-1"
                  :class="`${contentClass} ${themeSettingsStore.isDark ? 'custom-scrollbar-dark' : 'custom-scrollbar-light'}`"
              >
                <slot/>
              </div>
              <div
                  class="px-4 py-3 flex justify-end space-x-3 border-t border-slate-100 dark:border-slate-700"
                  v-if="$slots.footer"
              >
                <slot name="footer"></slot>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script>
import Icon from "@/components/Icon/index.vue";
import {useThemeSettingsStore} from "@/stores/themeSettings.js";

import {
  TransitionRoot,
  TransitionChild,
  Dialog,
  DialogPanel,
} from "@headlessui/vue";
import {defineComponent, ref, watch} from "vue";

export default defineComponent({
  components: {
    Icon,
    TransitionRoot,
    TransitionChild,
    Dialog,
    DialogPanel,
  },

  props: {
    labelClass: {
      type: String,
      default: "btn-primary",
    },
    centered: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: "Basic Modal",
    },
    label: {
      type: String,
      default: "Basic Modal",
    },
    disableBackdrop: {
      type: Boolean,
      default: false,
    },
    noFade: {
      type: Boolean,
      default: false,
    },
    themeClass: {
      type: String,
      default:
          "bg-slate-900 dark:bg-slate-800 dark:border-b dark:border-slate-700",
    },
    sizeClass: {
      type: String,
      default: "max-w-xl",
    },
    scrollContent: {
      type: Boolean,
      default: false,
    },
    activeModal: {
      type: Boolean,
      default: false,
    },
    hasButton: {
      type: Boolean,
      default: true,
    },
    contentClass: {
      type: String,
      default: 'max-h-[400px]'
    },
    footerClass: {
      type: String,
      default: ''
    }
  },
  emits: ['closeModal'],
  setup(props, {emit}) {
    const isOpen = ref(props.activeModal);

    // open
    const openModal = () => {
      isOpen.value = !isOpen.value;
    };
    // close
    const closeModal = () => {
      console.log('closeModal in modal component ')
      isOpen.value = false;
      emit('closeModal', isOpen)
    };

    watch(
        () => props.activeModal,
        (newValue) => {
          isOpen.value = newValue;
        }
    );

    const themeSettingsStore = useThemeSettingsStore()

    return {closeModal, openModal, isOpen, themeSettingsStore};
  },
});
</script>
<style scoped>
.custom-scrollbar::-webkit-scrollbar {
  width: 12px; /* Width of the scrollbar */
}

.custom-scrollbar::-webkit-scrollbar-track {
  border-radius: 10px;
}

.custom-scrollbar-dark::-webkit-scrollbar-track {
  background: #1E293B;
}

.custom-scrollbar-dark:hover::-webkit-scrollbar-track {
  background: #0F172A;
}

.custom-scrollbar-dark:hover::-webkit-scrollbar-thumb {
  background-color: #27364d; /* Thumb color */
  border-radius: 10px; /* Roundness of the scrollbar thumb */
  border: 3px solid #0F172A; /* Padding around the scrollbar thumb */
}

.custom-scrollbar-dark:hover::-webkit-scrollbar-thumb:hover {
  background: #344869; /* Thumb hover color */
}

.custom-scrollbar-light::-webkit-scrollbar-track {
  background: #FFF;
}

.custom-scrollbar-light:hover::-webkit-scrollbar-track {
  background: #e3e3e3;
}

.custom-scrollbar-light:hover::-webkit-scrollbar-thumb {
  background-color: #b2b2b2; /* Thumb color */
  border-radius: 10px; /* Roundness of the scrollbar thumb */
  border: 3px solid #e3e3e3; /* Padding around the scrollbar thumb */
}

.custom-scrollbar-light:hover::-webkit-scrollbar-thumb:hover {
  background: #969595; /* Thumb hover color */
}
</style>